/**
 * Two's-complement encoding and decoding at 4 to 64 bits, with the working a course shows.
 *
 * A width-N pattern with sign bit s and lower bits b has the value
 *   −s · 2^(N−1) + Σ b_i · 2^i,
 * so the range is −2^(N−1) … 2^(N−1) − 1, one more negative value than positive. Negating
 * is "invert every bit, then add 1", and because that operation undoes itself, the same
 * three rows explain both directions.
 */

import {
  BIG_ONE,
  BIG_ZERO,
  parseBigIntToken,
} from "@/lib/tool-kit/math/bigint";
import {
  bitLength,
  maskOf,
  patternBits,
  patternHex,
  toPattern,
  toSigned,
} from "./bits";

export const MIN_WIDTH = 4;
export const MAX_WIDTH = 64;

const SEPARATORS = /[\s_]/g;
const BINARY_PREFIX = /^0[bB]/;
const HEX_PREFIX = /^0[xX]/;
const BINARY_DIGITS = /^[01]+$/;
const HEX_DIGITS = /^[0-9a-fA-F]+$/;

// biome-ignore-start lint/suspicious/noBitwiseOperators: two's complement is bit arithmetic by definition

export interface WidthRange {
  min: bigint;
  max: bigint;
  /** Number of distinct patterns, 2^N. */
  count: bigint;
}

export function widthRange(width: number): WidthRange {
  const half = BIG_ONE << BigInt(width - 1);
  return { min: -half, max: half - BIG_ONE, count: BIG_ONE << BigInt(width) };
}

/** Whether `width` is an integer inside the supported 4–64 span. */
export function validWidth(width: number | null): width is number {
  return (
    width !== null &&
    Number.isInteger(width) &&
    width >= MIN_WIDTH &&
    width <= MAX_WIDTH
  );
}

/** The fewest bits that hold `value` in two's complement (at least 1). */
export function minimumWidth(value: bigint): number {
  if (value >= BIG_ZERO) {
    return bitLength(value) + 1;
  }
  return bitLength(-value - BIG_ONE) + 1;
}

export interface NegateSteps {
  /** The N-bit pattern the steps start from. */
  start: string;
  /** Every bit flipped (the one's complement). */
  inverted: string;
  /** The inverted pattern plus one, wrapped to N bits. */
  result: string;
}

/** Invert-and-add-one on an N-bit pattern. Negating twice gives the pattern back. */
export function negateSteps(pattern: bigint, width: number): NegateSteps {
  const inverted = ~pattern & maskOf(width);
  return {
    start: patternBits(pattern, width),
    inverted: patternBits(inverted, width),
    result: patternBits(toPattern(inverted + BIG_ONE, width), width),
  };
}

export interface Term {
  /** Bit position, 0 = least significant. */
  position: number;
  /** Signed weight: negative for the sign bit. */
  weight: bigint;
}

/** The weights of the set bits: −2^(N−1) for the sign bit, +2^i for the rest. */
export function setBitTerms(pattern: bigint, width: number): Term[] {
  const terms: Term[] = [];
  for (let position = width - 1; position >= 0; position--) {
    if (((pattern >> BigInt(position)) & BIG_ONE) === BIG_ONE) {
      const weight = BIG_ONE << BigInt(position);
      terms.push({
        position,
        weight: position === width - 1 ? -weight : weight,
      });
    }
  }
  return terms;
}

export interface Representations {
  signMagnitude: string | null;
  onesComplement: string | null;
  twosComplement: string;
}

/** The same value in the three classical signed formats; null where it has no pattern. */
export function representations(value: bigint, width: number): Representations {
  const limit = (BIG_ONE << BigInt(width - 1)) - BIG_ONE;
  const magnitude = value < BIG_ZERO ? -value : value;
  const twos = patternBits(toPattern(value, width), width);
  if (magnitude > limit) {
    return { signMagnitude: null, onesComplement: null, twosComplement: twos };
  }
  const signBit = value < BIG_ZERO ? BIG_ONE << BigInt(width - 1) : BIG_ZERO;
  const ones = value < BIG_ZERO ? ~magnitude & maskOf(width) : magnitude;
  return {
    signMagnitude: patternBits(signBit | magnitude, width),
    onesComplement: patternBits(ones, width),
    twosComplement: twos,
  };
}

export interface Analysis {
  width: number;
  /** What the reader typed, as a number: the decimal, or the pattern read as signed. */
  entered: bigint;
  /** Whether `entered` fits the range. A pattern always does. */
  fits: boolean;
  /** The N-bit pattern that is actually stored (wrapped if it did not fit). */
  pattern: bigint;
  bits: string;
  hex: string;
  /** The stored pattern read as a signed number. */
  signed: bigint;
  /** The stored pattern read as an unsigned number. */
  unsigned: bigint;
  negative: boolean;
  range: WidthRange;
  /** The fewest bits that hold the entered value. */
  neededWidth: number;
  terms: Term[];
  /** Invert-and-add-one from the stored pattern to the magnitude (negative values only). */
  negation: NegateSteps | null;
  /** Invert-and-add-one from the magnitude to the stored pattern (negative values only). */
  fromMagnitude: NegateSteps | null;
  forms: Representations;
  /** The same value sign-extended to twice the width, when that is 64 bits or fewer. */
  extended: string | null;
}

function analysePattern(
  pattern: bigint,
  width: number,
  entered: bigint
): Analysis {
  const signed = toSigned(pattern, width);
  const range = widthRange(width);
  const negative = signed < BIG_ZERO;
  return {
    width,
    entered,
    fits: entered >= range.min && entered <= range.max,
    pattern,
    bits: patternBits(pattern, width),
    hex: patternHex(pattern, width),
    signed,
    unsigned: pattern,
    negative,
    range,
    neededWidth: minimumWidth(entered),
    terms: setBitTerms(pattern, width),
    negation: negative ? negateSteps(pattern, width) : null,
    fromMagnitude: negative ? negateSteps(-signed, width) : null,
    forms: representations(signed, width),
    extended:
      width * 2 <= MAX_WIDTH
        ? patternBits(toPattern(signed, width * 2), width * 2)
        : null,
  };
}

/** Encode a signed decimal. A value outside the range wraps, as it would in hardware. */
export function encodeDecimal(value: bigint, width: number): Analysis {
  return analysePattern(toPattern(value, width), width, value);
}

/** Decode a pattern: the stored bits and the signed number they mean. */
export function decodePattern(pattern: bigint, width: number): Analysis {
  return analysePattern(pattern, width, toSigned(pattern, width));
}

export type PatternError = "empty" | "invalid" | "tooWide";

export type PatternParse =
  | { ok: true; pattern: bigint }
  | { ok: false; error: PatternError };

/** Read a binary or hex bit pattern. Shorter patterns are padded with zeros on the left. */
export function parsePattern(
  text: string,
  format: "binary" | "hex",
  width: number
): PatternParse {
  const cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  const isBinary = format === "binary";
  const digits = cleaned.replace(isBinary ? BINARY_PREFIX : HEX_PREFIX, "");
  if (!(isBinary ? BINARY_DIGITS : HEX_DIGITS).test(digits)) {
    return { ok: false, error: "invalid" };
  }
  const value = BigInt(`${isBinary ? "0b" : "0x"}${digits}`);
  if (bitLength(value) > width) {
    return { ok: false, error: "tooWide" };
  }
  return { ok: true, pattern: value };
}

export type DecimalParse =
  | { ok: true; value: bigint }
  | { ok: false; error: "empty" | "invalid" };

/** Read a whole decimal number of any size; spaces and underscores group digits. */
export function parseDecimal(text: string): DecimalParse {
  const cleaned = text.replace(SEPARATORS, "");
  if (cleaned.length === 0) {
    return { ok: false, error: "empty" };
  }
  const value = parseBigIntToken(cleaned);
  return value === null ? { ok: false, error: "invalid" } : { ok: true, value };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the two's complement helpers
